import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import EventDTO from '../models/event';
import { OrderItemData } from '../models/order';
import { OrderData, useGetOrderTotalsQuery } from '../store/apis/orders';
import { formatCurrency } from '../utils/shared';
import OrderTotalSkeleton from './OrderTotalSkeleton';
import { parseFriendlyDateAndHour } from '../utils/dates';

interface Props {
  items: OrderItemData[];
  customized: boolean;
  event: EventDTO;
  order: OrderData;
  onCalculatedTotalToPay: (total: number) => void;
  onLoadingTotals: (loading: boolean) => void;
  hasOpenPrice?: boolean;
}

const OrderSummary: React.FC<Props> = ({
  items, customized, event, order, onCalculatedTotalToPay, onLoadingTotals, hasOpenPrice,
}) => {
  const { data: orderTotals, isFetching } = useGetOrderTotalsQuery({
    businessId: event.business_id,
    eventId: event.id,
    order,
  }, { skip: (hasOpenPrice && !order.open_price_amount) || !event });

  const location = useLocation();
  const isBackoffice = location.pathname.includes('/admin/');

  useEffect(() => {
    if (orderTotals) {
      onCalculatedTotalToPay(orderTotals.total_amount);
    }
  }, [orderTotals]);

  useEffect(() => {
    onLoadingTotals(isFetching);
  }, [isFetching]);

  return (
    <div className="flex flex-col glass-surface rounded-3xl w-full p-3 gap-4">
      <h1 className="text-lg font-semibold">Resumen</h1>
      <div className="flex flex-col border border-border-secondary rounded-3xl">
        {items.map((item, index) => (
          <div className={`text-secondary text-text-secondary border-b border-border-secondary bg-opacity-medium ${index === 0 ? 'rounded-t-3xl' : ''} p-4`} key={index}>
            <div>
              {customized && <div className="flex flex-row">
                <div className="font-medium">{item.name} {item.last_name}</div>
                <div className='ml-auto'>
                  {!hasOpenPrice && !item.audience_discount_id && <span>{formatCurrency(item.ticket.price)}</span>}
                </div>
                {item.audience_discount_id && <span className='font-light line-through ml-auto mr-4'>
                  {formatCurrency(item.ticket.price)}
                  </span>}
                {item.audience_discount_id && <span>
                  {formatCurrency(item.ticket.price * (1 - (item.audience_discount?.percentage || 0)))}
                </span>}
              </div>}
              <div className="flex justify-between items-center">
                <span>
                  {!item.ticket_extra && <span> {item.ticket.name} {event.has_numerated_seats ? ` / Entrada ${item.seat_name}` : ''} {item.ticket.showtime_id ? ` / Función: ${parseFriendlyDateAndHour(event?.showtimes?.find((st) => st.id === item.ticket.showtime_id)?.starts_at ?? '', true)}` : ''}</span>}
                  {item.ticket_extra && <span> {item.ticket.name} / Extra</span>}
                </span>
                {!customized && !hasOpenPrice && <span>{formatCurrency(item.ticket.price)}</span>}
              </div>
            </div>
          </div>
        ))}
        <div className="rounded-b-3xl bg-opacity-heavy">
          {isFetching && <OrderTotalSkeleton />}
          {(orderTotals && !isFetching && !isBackoffice) && (
            <div>
              <div className="flex flex-col">
                <span className="p-[16px] flex flex-row">
                  Subtotal{' '}
                  <span
                    className={`${orderTotals.subtotal < orderTotals.base_tickets_total ? 'line-through font-light ml-auto mr-4' : 'ml-auto'
                    }`}
                  >
                    {formatCurrency(orderTotals.base_tickets_total)}
                  </span>{' '}
                  {(orderTotals.subtotal !== orderTotals.base_tickets_total)
                    && formatCurrency(orderTotals.subtotal)}
                </span>
                {!event.tickets_includes_service_charge && (
                  <span className="p-[16px] flex flex-row justify-between">
                    <div>Service charge{' '}</div>
                    <div>{formatCurrency(orderTotals.service_charge_fee)}</div>
                  </span>
                )}
                {(event.tickets_includes_service_charge && orderTotals.payment_method_discounts > 0) && (
                  <span className="p-[16px] flex flex-row justify-between">
                    <div>Descuento por medio de pago{' '}</div>
                    <div>{formatCurrency(orderTotals.payment_method_discounts)}</div>
                  </span>
                )}
                <span className="font-medium p-[16px] flex flex-row justify-between">
                  <div>Total a pagar</div>
                  <div>{formatCurrency(orderTotals.total_amount)}</div>
                </span>
              </div>
            </div>
          )}
          {(orderTotals && !isFetching && isBackoffice) && (
            <div className="flex flex-col">
              <span className="font-medium p-[16px] flex flex-row justify-between">
                <div>Total a pagar</div>
                <div>{formatCurrency(orderTotals.total_amount)}</div>
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default OrderSummary;
